import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import GoogleIcon from '@mui/icons-material/Google'
import horizontalLogo from '../../assets/images/horizontal.svg'
import { useAuth } from '../../shared/context/AuthContext'

/**
 * Port of `app/register/login.html` + `app/register/loginController.js` as
 * they exist AFTER commit a6c16fa ("feat(login): troca login para
 * firebase") — that commit removed the username/password form entirely and
 * left only a single "Entrar com Google" button (Google Workspace OAuth via
 * Firebase Auth is the sole login method going forward; legacy password
 * login is disabled server-side by default, see
 * `server_app/routes/userRouter.js`'s `LEGACY_PASSWORD_LOGIN_ENABLED`
 * gate). Wired to `AuthContext.loginWithGoogle()` (task 009) instead of the
 * legacy `AuthService` + `$cookies`.
 *
 * Error message: ported from `loginController.js`'s `loginWithGoogle`
 * catch block, which prefers the backend's `error.data.errorMessage`, then
 * `error.message`, falling back to a generic message — shown here via an
 * MUI `Alert` (the legacy app additionally used `Materialize.toast(msg,
 * 5000)`, which has no MUI equivalent already established elsewhere in
 * this codebase; the `Alert` carries the same message content).
 *
 * Deliberately NOT ported from the legacy template:
 * - The decorative animated SVG background (`login.html`'s inline
 *   `<script>` drawing/animating a triangle mesh over `.wraper-login`) is
 *   pure visual chrome unrelated to the login flow itself, and the "UI Kit"
 *   decision in `specs/react-migration/plan.md` (Materialize -> MUI) is
 *   explicitly not a 1:1 visual port.
 * - `$scope.register` / `$state.go('register')`: confirmed dead code (no
 *   caller in the template, no `register` state in `app/main.js`).
 */

// Where a successful login lands, ported from `loginController.js`'s
// `$state.go('workspace')` — `workspace` alone has no content of its own
// (see `AppRoutes.jsx` notes), so the closest equivalent landing screen is
// its first real child route.
const POST_LOGIN_PATH = '/workspace/customers'

const DEFAULT_ERROR_MESSAGE = 'Não foi possível autenticar com Google.'

/** Mirrors `loginController.js`'s error-message fallback chain exactly. */
function getGoogleLoginErrorMessage(error) {
  if (error?.response?.data?.errorMessage) {
    return error.response.data.errorMessage
  }
  if (error?.message) {
    return error.message
  }
  return DEFAULT_ERROR_MESSAGE
}

function LoginPage() {
  const { loginWithGoogle } = useAuth()
  const navigate = useNavigate()

  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  async function handleGoogleLogin() {
    setErrorMessage(null)
    setSubmitting(true)
    try {
      await loginWithGoogle()
      navigate(POST_LOGIN_PATH, { replace: true })
    } catch (error) {
      setErrorMessage(getGoogleLoginErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'primary.dark',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src={horizontalLogo} alt="Astrea" style={{ maxWidth: '100%', height: 'auto' }} />
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Button
            type="button"
            name="btn_login"
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGoogleLogin}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
          >
            Entrar com Google
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

export default LoginPage
